import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ListItem } from '../../types/bills/ListItem';
import { ListService } from '../../services/list.service';
import { AuthService } from '../../services/auth.service';
import { List } from '../../types/list';
import { map } from 'rxjs/operators';

@Component({
    selector: 'las-list-management-modal',
    templateUrl: './list-management-modal.component.html',
    styleUrls: ['./list-management-modal.component.scss'],
    standalone: false,
})
export class ListManagementModalComponent implements OnInit {
    readonly lists: ListDetails[] = [
        {
            title: ListType[ListType.Personal],
            isLoading: true,
            list: [],
        },
        {
            title: ListType[ListType.Department],
            isLoading: true,
            list: [],
        },
    ];

    billId: string;
    measure: string;
    isLoading = true;
    isSaving = false;

    constructor(
        private dialog: MatDialogRef<ListManagementModalComponent>,
        private listService: ListService,
        private auth: AuthService,
        @Inject(MAT_DIALOG_DATA) data: ModalData,
    ) {
        this.billId = data.billId;
        this.measure = data.measure;
    }

    /**
     * @see AuthService.isAdmin
     */
    get isAdmin() {
        return this.auth.isAdmin();
    }

    /**
     * Returns true if all lists have finished loading; false otherwise.
     */
    get areListsLoading() {
        return this.lists
            .map((detail) => detail.isLoading)
            .reduce((p, v) => p || v);
    }

    /**
     * Gets the department list from the lists array.
     */
    get departmentList() {
        return this.lists[ListType.Department];
    }

    /**
     * Gets the personal list from the lists array.
     */
    get personalList() {
        return this.lists[ListType.Personal];
    }

    /**
     * Loads both lists if the user is an admin; otherwise just the personal list.
     */
    ngOnInit(): void {
        this.getListType(this.personalList.title).subscribe((list) =>
            this.setList(this.personalList, list),
        );

        const departmentList = this.departmentList;
        if (this.isAdmin) {
            this.getListType(departmentList.title).subscribe((list) =>
                this.setList(departmentList, list),
            );
        } else {
            this.lists.pop();
        }
    }

    /**
     * Updates the details for a given list and sets isLoading.
     * @param detail The mapped list details.
     * @param list The list retrieved from the api.
     */
    setList(detail: ListDetails, list: List[]) {
        detail.list = list;
        detail.isLoading = false;
        this.isLoading = this.areListsLoading;
    }

    /**
     * Loads the list from the api (cache is invalidated) with its items.
     * @param type The type of list. (i.e., Personal)
     */
    getListType = (type: string) =>
        this.listService
            .getByType(type, true, true)
            .pipe(map((lists) => this.mapChecked(lists)));

    /**
     * Checks the list's list item if any item's id matches the bill's id.
     * @param lists An array of managed lists.
     */
    mapChecked = (lists: List[]) =>
        lists.map((list) => {
            list.checked = false;
            const items = list.listItems;
            for (let item of items) {
                if (item.billID === this.billId) {
                    list.checked = true;
                    break;
                }
            }
            return list;
        });

    /**
     * Save button click listener that appends both lists together and saves them.
     */
    onSave() {
        this.isSaving = true;

        this.listService
            .saveLists(this.billId, [
                ...(this.departmentList?.list ?? []),
                ...this.personalList.list,
            ])
            .subscribe((_) => {
                this.isSaving = false;
                this.dialog.close(true);
            });
    }
}

enum ListType {
    Personal,
    Department,
}

type ModalData = { billId: string; measure: string };
type ListDetails = {
    list: List[];
    title: string;
    isLoading: boolean;
};
